import React from 'react';

/**
 * Reusable SVG Icon Components
 * Used throughout the app to replace emojis with consistent vector icons
 */

// Message/Chat Icon (replaces 💬)
export const MessageIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// User/Person Icon (replaces 👤)
export const UserIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

// Pin Icon (replaces 📌)
export const PinIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
  </svg>
);

// Eye Icon (replaces 👁️)
export const EyeIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

// Eye Off Icon (for hiding, replaces 👁️ context)
export const EyeOffIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-4.803m5.596-3.856a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Checkmark Icon (replaces ✓)
export const CheckIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
);

// Heart Icon (replaces ❤️)
export const HeartIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none", isFilled = true }) => (
  <svg
    className={className}
    fill={isFilled ? fill : "none"}
    stroke={isFilled ? "none" : "currentColor"}
    viewBox="0 0 24 24"
    strokeWidth={isFilled ? 0 : 2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// Bell Icon (replaces 🔔)
export const BellIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0018 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

// Search Icon (replaces 🔍)
export const SearchIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Lock Icon (replaces 🔒)
export const LockIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// Settings/Gear Icon (replaces ⚙️)
export const SettingsIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Close/X Icon (replaces ❌)
export const CloseIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Plus Icon
export const PlusIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

// Lightbulb Icon (replaces 💡)
export const LightbulbIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5.364 6.364l-.707.707M9 19.071V20m4-4h.01" />
  </svg>
);

// Star Icon (replaces ⭐)
export const StarIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none", isFilled = true }) => (
  <svg
    className={className}
    fill={isFilled ? fill : "none"}
    stroke={isFilled ? "none" : "currentColor"}
    viewBox="0 0 24 24"
    strokeWidth={isFilled ? 0 : 2}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Comment Icon (replaces 💭)
export const CommentIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-2H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 2z" />
  </svg>
);

// Reply/Return Arrow Icon (replaces ↩️)
export const ReplyIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-4" />
  </svg>
);

// Mention Icon (for @)
export const MentionIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Apple/Food Icon
export const AppleIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 2C9.5 2 7.5 4 7.5 6.5c0 2 1 3.5 2 4.5V20c0 1.1.9 2 2 2h1c1.1 0 2-.9 2-2v-7.5c1-1 2-2.5 2-4.5C16.5 4 14.5 2 12 2zm1 18h-2v-5h2v5z" />
  </svg>
);

// Chair Icon
export const ChairIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M6 2h12v4H6V2zm2 4h8v10h-2v6H8v-6H6V6zm1 2v6h6V8H9z" />
  </svg>
);

// Pasta Icon
export const PastaIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11z" />
  </svg>
);

// Gift Icon
export const GiftIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v-1m0 0H8m4 0h4m-8 8v4h12v-4m-6-8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2m12 0V6a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
  </svg>
);

// Lightning/Bolt Icon (for ⚡)
export const BoltIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

// Shield Icon (for security)
export const ShieldIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

// Home/House Icon
export const HomeIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
  </svg>
);

// Calendar Icon
export const CalendarIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Question Mark Icon
export const QuestionIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Handshake Icon (for partnership)
export const HandshakeIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1m2-1v2.5m-2-2.5l-2 1m2-1l-2-1m2 1v2.5m6-6l-2-1-2 1m2-1v2.5m-2-2.5l-2 1m2-1l-2-1m2 1v2.5" />
  </svg>
);

// ID Card Icon (replaces 🪪)
export const IdCardIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-5 9h10" />
  </svg>
);

// Phone Icon (replaces 📞)
export const PhoneIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 00.948.684l1.498 4.493a1 1 0 00.502.756l2.048 1.029a11.04 11.04 0 005.516 5.516l1.029 2.048a1 1 0 00.756.502l4.493 1.498a1 1 0 00.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

// Flag Icon (replaces 🚩)
export const FlagIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-7 7 7 7h-12.5l-1-1H5a2 2 0 00-2 2z" />
  </svg>
);

// Key Icon (replaces 🔑)
export const KeyIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012-2h2.586a1 1 0 00.707-.293l-2.414-2.414a1 1 0 00-1.414 0l-2.829 2.829a1 1 0 00 0 1.414L12.586 7H10a2 2 0 00-2 2m7-7l2.414 2.414a2 2 0 010 2.828l-.828.828-2.828-2.829.828-.828a2 2 0 012.828 0z" />
  </svg>
);

// Package/Box Icon
export const PackageIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m0 0L4 7m8 4v10l8-4V11m-8 4L4 11v10l8 4z" />
  </svg>
);

// Bed Icon (replaces 🛏️)
export const BedIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18m0 0V7a2 2 0 00-2-2H5a2 2 0 00-2 2v3m18 0H3m0 0v8a2 2 0 002 2h14a2 2 0 002-2v-8z" />
  </svg>
);

// Ruler Icon (replaces 📏)
export const RulerIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h16v2H4V4zm0 4h16v2H4V8zm0 4h16v2H4v-2zm0 4h16v2H4v-2z" />
  </svg>
);

// Shower Icon (replaces 🚿)
export const ShowerIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2zm3 2v8m3-8v8m3-8v8" />
  </svg>
);

// Building Icon (replaces 🏢)
export const BuildingIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4l2-3h2l2 3h4a2 2 0 012 2v14a2 2 0 01-2 2z" />
  </svg>
);

// Parking Icon (replaces 🅿️)
export const ParkingIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

// Paw Icon (replaces 🐾)
export const PawIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 21c1.5 0 3-1 4-2 2-2 3-3 4-4 1-1 2-2 2-3 0-2-1.5-3.5-3.5-3.5-1 0-2 .5-2.5 1.5-.5-1-1.5-1.5-2.5-1.5-1 0-2 .5-2.5 1.5-.5-1-1.5-1.5-2.5-1.5C5.5 8 4 9.5 4 11.5c0 1 1 2 2 3 1 1 2 2 4 4 1 1 2.5 2 2 2z" />
  </svg>
);

// Location/Map Pin Icon (replaces 📍)
export const LocationIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Document/Notepad Icon (replaces 📝)
export const DocumentIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// Money/Currency Icon (replaces 💰)
export const MoneyIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Sparkle Icon (replaces ✨)
export const SparkleIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// People/Users Icon (replaces 👥)
export const PeopleIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.856-1.487M15 10a3 3 0 11-6 0 3 3 0 016 0zM9 20H4v-2a6 6 0 0112 0v2H9z" />
  </svg>
);

// Clipboard/List Icon (replaces 📋)
export const ClipboardIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3" />
  </svg>
);

// Camera Icon (replaces 📸)
export const CameraIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Mailbox Icon (replaces 📬)
export const MailboxIcon = ({ className = "w-5 h-5", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M19 6h-2V4a2 2 0 00-2-2H9a2 2 0 00-2 2v2H5a3 3 0 00-3 3v8a3 3 0 003 3h14a3 3 0 003-3V9a3 3 0 00-3-3zm-8-2h4v2h-4V4z" />
  </svg>
);

// Mail Icon (replaces 📧)
export const MailIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Trash Icon (replaces 🗑️)
export const TrashIcon = ({ className = "w-5 h-5", fill = "none", stroke = "currentColor" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

